import { SignInUser } from "@/types/user";
import type { CompanyUser } from "@/types/user";
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface UserStoreState {
  user: SignInUser | null;
  /** Full user from ME API (company-users/me); includes company/store for use anywhere */
  me: CompanyUser | null;
  signIn: (user: SignInUser) => void;
  signOut: () => void;
  setMe: (me: CompanyUser | null) => void;
}

let userStoreHasHydrated = false;
const userStoreHydrationListeners = new Set<() => void>();

const notifyUserStoreHydrated = () => {
  userStoreHasHydrated = true;
  userStoreHydrationListeners.forEach((listener) => listener());
  userStoreHydrationListeners.clear();
};

/** Subscribe to persist rehydration (zustand 4.x has no runtime persist.hasHydrated). */
export const subscribeUserStoreHydration = (listener: () => void) => {
  if (userStoreHasHydrated) {
    listener();
    return () => undefined;
  }
  userStoreHydrationListeners.add(listener);
  return () => {
    userStoreHydrationListeners.delete(listener);
  };
};

export const getUserStoreHasHydrated = () => userStoreHasHydrated;

const useUserStore = create<UserStoreState>()(
  persist(
    (set) => ({
      user: null,
      me: null,
      signIn: (user) => set({ user }),
      signOut: () => set({ user: null, me: null }),
      setMe: (me) => set({ me }),
    }),
    {
      name: "user",
      onRehydrateStorage: () => () => {
        notifyUserStoreHydrated();
      },
    },
  ),
);

export const userActionOutsideOfComponent = useUserStore.setState;

export default useUserStore;
